import * as Headless from '@headlessui/react'
import clsx from 'clsx'
import { ChevronUp } from 'lucide-react'
import React from 'react'

import { Accordion } from '@ankor-io/ui-components/src/Accordion'
import { TouchTarget } from '@ankor-io/ui-components/src/Button'
import { Link } from '@ankor-io/ui-components/src/Link'

type SidebarNavSectionColor = 'orange' | 'primary'

export type NavItem = {
  title: string
  url: string
  icon: React.ElementType
  disabled?: boolean
  tooltip?: string
}

type SidebarNavSectionProps = {
  title: string
  color: SidebarNavSectionColor
  defaultExpanded?: boolean
  items: NavItem[]
  cta?: React.ReactNode
  currentPath: string
}

const colorClasses: Record<SidebarNavSectionColor, string> = {
  orange: 'text-orange-500',
  primary: 'text-blue-600',
}

const activeColorClasses: Record<SidebarNavSectionColor, string> = {
  orange: 'bg-orange-400/20 dark:bg-orange-500/20',
  primary: 'bg-blue-400/20 dark:bg-blue-500/20',
}

const isNavItemActive = (item: NavItem, pathname: string) =>
  pathname === item.url || (item.url !== '/' && pathname.startsWith(item.url))

const navLinkClassName = (color: SidebarNavSectionColor, current: boolean) =>
  clsx(
    'transition-all flex w-full items-center gap-3 rounded-lg p-2 text-left font-medium text-xs text-gray-900 dark:text-white',
    '*:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:text-gray-900 dark:*:data-[slot=icon]:text-white',
    'data-hover:bg-gray-200 dark:data-hover:bg-gray-700',
    'data-disabled:opacity-50 data-disabled:cursor-not-allowed',
    current && [activeColorClasses[color], 'text-gray-900 dark:text-white'],
  )

const renderNavItems = (
  items: NavItem[],
  currentPath: string,
  color: SidebarNavSectionColor,
  cta?: React.ReactNode,
) => (
  <div className="flex flex-col gap-0.5">
    {cta}
    {items.map((item) => {
      const current = isNavItemActive(item, currentPath)
      const isDisabled = item.disabled ?? false
      const showTooltip = isDisabled && !!item.tooltip

      return (
        <span
          key={item.url}
          className="relative"
          data-tooltip-id={showTooltip ? 'sidebar-disabled-tooltip' : undefined}
          data-tooltip-content={showTooltip ? item.tooltip : undefined}
        >
          <Headless.CloseButton
            as={Link}
            to={item.url}
            disabled={isDisabled}
            aria-label={item.title}
            className={navLinkClassName(color, current)}
            data-current={current ? 'true' : undefined}
          >
            <TouchTarget>
              <item.icon className="size-4" />
              <span className="truncate text-xs">{item.title}</span>
            </TouchTarget>
          </Headless.CloseButton>
        </span>
      )
    })}
  </div>
)

type SidebarNavLinksProps = {
  items: NavItem[]
  currentPath: string
  color: SidebarNavSectionColor
}

export const SidebarNavLinks = ({ items, currentPath, color }: SidebarNavLinksProps) =>
  renderNavItems(items, currentPath, color)

export const SidebarNavSection = ({
  title,
  color,
  defaultExpanded = true,
  items,
  cta,
  currentPath,
}: SidebarNavSectionProps) => {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      trigger={({ isExpanded }) => (
        <>
          <span className={clsx('text-xs font-semibold uppercase tracking-wider', colorClasses[color])}>{title}</span>
          <ChevronUp
            className={clsx(
              'size-4 transition-transform duration-200',
              colorClasses[color],
              !isExpanded && 'rotate-180',
            )}
          />
        </>
      )}
    >
      {renderNavItems(items, currentPath, color, cta)}
    </Accordion>
  )
}
